import React, { useContext, useRef, useState } from "react";
import "./cropper.css";
import CancelIcon from "@mui/icons-material/Cancel";
import Cropper from "react-easy-crop";
import { useDispatch } from "react-redux";
import heic2any from "heic2any";

import { snackbarContext } from "../../helpers/Snakbar";
import getCroppedImg from "../../helpers/cropImage";
import {
  uploadAlbum,
  uploadBanner,
} from "../../../redux/reducers/profileSlice";
import BackDrop from "../../helpers/BackDrop";

const MAX_INPUT_SIZE = 10 * 1024 * 1024;
const MAX_OUTPUT_SIZE = 1.5 * 1024 * 1024;
const MAX_OUTPUT_DIMENSION = 1600;
const MIN_OUTPUT_DIMENSION = 320;

const ALLOWED_EXTENSIONS = new Set([
  ".jpeg",
  ".jpg",
  ".png",
  ".webp",
  ".heif",
  ".heic",
]);

const getFileExtension = (fileName = "") => {
  const lastDot = fileName.lastIndexOf(".");
  return lastDot >= 0 ? fileName.slice(lastDot).toLowerCase() : "";
};

const isHeicFile = (file, extension) => {
  const mimeType = (file.type || "").toLowerCase();

  return (
    extension === ".heic" ||
    extension === ".heif" ||
    mimeType === "image/heic" ||
    mimeType === "image/heif" ||
    mimeType === "image/heic-sequence" ||
    mimeType === "image/heif-sequence"
  );
};

const convertHeicToJpeg = async (file) => {
  const converted = await heic2any({
    blob: file,
    toType: "image/jpeg",
    quality: 0.9,
  });

  // heic2any can return an array when the source contains multiple images.
  const jpegBlob = Array.isArray(converted) ? converted[0] : converted;

  if (!(jpegBlob instanceof Blob) || jpegBlob.size === 0) {
    throw new Error("HEIC conversion returned an empty image");
  }

  return new File([jpegBlob], "converted-image.jpg", {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
};

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result !== "string" || !reader.result) {
        reject(new Error("Unable to read image data"));
        return;
      }

      resolve(reader.result);
    };

    reader.onerror = () => {
      reject(new Error("Unable to read the selected image"));
    };

    reader.onabort = () => {
      reject(new Error("Image reading was cancelled"));
    };

    reader.readAsDataURL(file);
  });

const canvasToJpegBlob = (canvas, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("JPEG compression failed"));
        }
      },
      "image/jpeg",
      quality
    );
  });

const canvasToCompressedFile = async (
  sourceCanvas,
  maxBytes = MAX_OUTPUT_SIZE
) => {
  if (
    !sourceCanvas ||
    sourceCanvas.width <= 0 ||
    sourceCanvas.height <= 0
  ) {
    throw new Error("The cropped image is empty");
  }

  let width = sourceCanvas.width;
  let height = sourceCanvas.height;

  const initialScale = Math.min(
    1,
    MAX_OUTPUT_DIMENSION / Math.max(width, height)
  );

  width = Math.max(1, Math.round(width * initialScale));
  height = Math.max(1, Math.round(height * initialScale));

  const outputCanvas = document.createElement("canvas");
  const context = outputCanvas.getContext("2d");

  if (!context) {
    throw new Error("Unable to prepare the image for upload");
  }

  let quality = 0.85;
  let blob;
  let attempts = 0;
  const maxAttempts = 40;

  while (attempts < maxAttempts) {
    attempts += 1;

    outputCanvas.width = width;
    outputCanvas.height = height;
    context.drawImage(sourceCanvas, 0, 0, width, height);

    blob = await canvasToJpegBlob(outputCanvas, quality);

    if (blob.size > 0 && blob.size <= maxBytes) {
      return new File([blob], "cropped-image.jpeg", {
        type: "image/jpeg",
        lastModified: Date.now(),
      });
    }

    if (quality > 0.55) {
      quality = Math.max(0.55, Number((quality - 0.1).toFixed(2)));
      continue;
    }

    // Reduce both dimensions proportionally, preserving the crop's aspect ratio.
    const longestDimension = Math.max(width, height);
    if (longestDimension <= MIN_OUTPUT_DIMENSION) {
      break;
    }

    const resizeScale = Math.max(
      MIN_OUTPUT_DIMENSION / longestDimension,
      0.8
    );

    width = Math.max(1, Math.round(width * resizeScale));
    height = Math.max(1, Math.round(height * resizeScale));
    quality = 0.75;
  }

  throw new Error(
    "Unable to reduce this image to the upload size limit. Please choose another image."
  );
};

const CropperCom = ({ handleCropper, setValues, values, imageType }) => {
  const inputRef = useRef(null);
  const setStateSnackbarContext = useContext(snackbarContext);
  const dispatch = useDispatch();

  const [showRecommendation, setShowRecommendation] = useState(true);
  const [image, setImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropArea, setCropArea] = useState(null);
  const [showBackDrop, setShowBackDrop] = useState(false);
  const [isProcessingFile, setIsProcessingFile] = useState(false);

  const onSelectFile = async (event) => {
    const input = event.target;
    const selectedFile = input.files?.[0];

    if (!selectedFile) {
      input.value = "";
      setStateSnackbarContext(true, "Please select an image", "warning");
      return;
    }

    const extension = getFileExtension(selectedFile.name);

    if (!ALLOWED_EXTENSIONS.has(extension)) {
      input.value = "";
      setStateSnackbarContext(true, "Invalid file type", "error");
      return;
    }

    if (selectedFile.size > MAX_INPUT_SIZE) {
      input.value = "";
      setStateSnackbarContext(
        true,
        "File size too big. Please select an image smaller than 10 MB.",
        "warning"
      );
      return;
    }

    if (isProcessingFile || showBackDrop) {
      input.value = "";
      return;
    }

    setIsProcessingFile(true);

    try {
      let previewFile = selectedFile;

      if (isHeicFile(selectedFile, extension)) {
        previewFile = await convertHeicToJpeg(selectedFile);
      }

      const imageDataUrl = await readFileAsDataUrl(previewFile);

      setImage(imageDataUrl);
      setShowRecommendation(false);
      setCropArea(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch {
      // Keep implementation details out of the user-facing message.
      setStateSnackbarContext(
        true,
        "Unable to process this image. Please try another image or export it as JPEG or PNG.",
        "error"
      );
    } finally {
      // Allows the same file to be selected again after an error or success.
      input.value = "";
      setIsProcessingFile(false);
    }
  };

  const onCropComplete = (_croppedAreaPercentage, croppedAreaPixels) => {
    setCropArea(croppedAreaPixels);
  };

  const handleScale = (event) => {
    const scale = Number.parseFloat(event.target.value);
    if (Number.isFinite(scale)) {
      setZoom(scale);
    }
  };

  const triggerFilePopup = () => {
    if (!showBackDrop && !isProcessingFile) {
      inputRef.current?.click();
    }
  };

  const onClear = () => {
    if (showBackDrop || isProcessingFile) return;

    setImage(null);
    setCropArea(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setShowRecommendation(true);
  };

  const onUpload = async () => {
    if (showBackDrop || isProcessingFile) return;

    if (!image) {
      setStateSnackbarContext(true, "Please select an image", "warning");
      return;
    }

    if (!cropArea) {
      setStateSnackbarContext(
        true,
        "Please wait for the crop area to load",
        "warning"
      );
      return;
    }

    setShowBackDrop(true);

    try {
      const croppedCanvas = await getCroppedImg(image, cropArea);
      const croppedFile = await canvasToCompressedFile(
        croppedCanvas,
        MAX_OUTPUT_SIZE
      );

      const fieldName = imageType;
      const formdata = new FormData();
      formdata.append(fieldName, croppedFile);

      if (imageType === "footerImage") {
        dispatch(
          uploadAlbum({
            formdata,
            setShowBackDrop,
            handleCropper,
            setStateSnackbarContext,
            setValues,
            values,
          })
        );
      } else {
        dispatch(
          uploadBanner({
            formdata,
            fieldName,
            handleCropper,
            setValues,
            values,
            setShowBackDrop,
            setStateSnackbarContext,
          })
        );
      }
    } catch (error) {
      setShowBackDrop(false);
      setStateSnackbarContext(
        true,
        error?.message || "Unable to process this image. Please try again.",
        "error"
      );
    }
  };

  const getRecommendationText = () => {
    switch (imageType) {
      case "bannerImage":
        return "Recommended crop: 2:3 portrait (e.g. 1200 × 1800 px)";
      case "footerImage":
        return "Recommended crop: 3:2 landscape (e.g. 1600 × 1067 px)";
      case "groomImage":
      case "brideImage":
        return "Recommended crop: 1:1 square (e.g. 1000 × 1000 px)";
      default:
        return "";
    }
  };

  return (
    <>
      <div className="default-bg-conatiner">
        <CancelIcon
          className="cancel-icon"
          onClick={showBackDrop || isProcessingFile ? undefined : handleCropper}
        />

        <input
          type="file"
          ref={inputRef}
          style={{ display: "none" }}
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,.jpg,.jpeg,.png,.webp"
          onChange={onSelectFile}
          disabled={showBackDrop || isProcessingFile}
        />

        <div className="container-cropper">
          {showRecommendation && (
            <div className="recommendation-text">
              {getRecommendationText()}
            </div>
          )}

          {image && (
            <>
              <div className="cropper">
                <Cropper
                  image={image}
                  crop={crop}
                  zoom={zoom}
                  aspect={
                    imageType === "bannerImage"
                      ? 2 / 3
                      : imageType === "footerImage"
                      ? 3 / 2
                      : 1
                  }
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>

              <div className="slider">
                <span>Zoom:</span>
                <input
                  type="range"
                  min={1}
                  max={5}
                  step={0.1}
                  value={zoom}
                  onChange={handleScale}
                  disabled={showBackDrop || isProcessingFile}
                />
              </div>
            </>
          )}
        </div>

        <div className="container-button">
          <button
            type="button"
            className="btn btn-primary options-buttons"
            style={{ marginRight: "5px" }}
            onClick={triggerFilePopup}
            disabled={showBackDrop || isProcessingFile}
          >
            CHOOSE
          </button>

          <button
            type="button"
            className="btn btn-secondary options-buttons"
            style={{ marginRight: "5px" }}
            onClick={onUpload}
            disabled={showBackDrop || isProcessingFile || !image || !cropArea}
          >
            UPLOAD
          </button>

          <button
            type="button"
            className="btn btn-danger options-buttons"
            onClick={onClear}
            disabled={showBackDrop || isProcessingFile}
          >
            CLEAR
          </button>
        </div>
      </div>

      {showBackDrop && <BackDrop />}
    </>
  );
};

export default CropperCom;
